import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { PREDEFINED_USER_ROLES, ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { NavigationHubMenuItem } from '../../components/navigation';
import { EmptyState, Screen } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const WarehouseView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { user, isAVC, isPicker } = useUserContext();

	const isAdminUser = useMemo(() => {
		return Boolean(user && (user.isSuperAdmin || (isAVC && user.role.name === PREDEFINED_USER_ROLES.ADMIN)));
	}, [isAVC, user]);

	const canScanProducts = isAdminUser;
	const canManageStocktakes = canScanProducts;
	const canViewStockDeliveries = useMemo(() => {
		return Boolean(isAdminUser && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_LIST_PAGE)));
	}, [isAdminUser, user]);
	const hasStockPagePermission = useMemo(() => {
		return Boolean(user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_STOCK_PAGE)));
	}, [user]);
	// Pickers process AVC driver returns without gaining access to the wider warehouse tools.
	const canViewPendingStock = Boolean(hasStockPagePermission || isPicker);
	const canViewPurchaseOrders = useMemo(() => {
		return Boolean(user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_LIST_PAGE)));
	}, [user]);
	const canViewStockOrPurchaseOrders = canViewStockDeliveries || canViewPurchaseOrders;
	const canViewWarehouseActions = Boolean(canScanProducts || canManageStocktakes || canViewPendingStock || canViewStockOrPurchaseOrders);

	const goTo = useCallback(screen => () => {
		navigation.navigate(screen);
	}, [navigation]);

	return (
		<Screen
			heading="Warehouse"
			subheading="Stock tools for warehouse counts, returns, deliveries and purchase orders."
		>
			<View style={styles.menuList}>
				{
					Boolean(canScanProducts) && (
						<NavigationHubMenuItem
							label="Product scanner"
							description="Add barcodes to products"
							icon="barcode-outline"
							onPress={goTo('BarcodeScannerView')}
						/>
					)
				}
				{
					Boolean(canManageStocktakes) && (
						<NavigationHubMenuItem
							label="Stocktakes"
							description="Count warehouse stock"
							icon="cube-outline"
							onPress={goTo('StocktakeListView')}
						/>
					)
				}
				{
					Boolean(canViewPendingStock) && (
						<NavigationHubMenuItem
							label="Pending stock"
							description="Process driver returns by run date"
							icon="file-tray-outline"
							onPress={goTo('PendingStockListView')}
						/>
					)
				}
				{
					Boolean(canViewStockOrPurchaseOrders) && (
						<NavigationHubMenuItem
							label="Purchase orders and deliveries"
							description="Manage purchase orders and stock deliveries"
							icon="repeat-outline"
							onPress={goTo('StockDeliveryListView')}
						/>
					)
				}
			</View>
			{
				!canViewWarehouseActions && (
					<EmptyState
						icon="package"
						title="No warehouse actions available"
					/>
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	menuList: {
		gap: space.sm,
	},
});

export default WarehouseView;
